import React, { FC, useEffect, useMemo, useState } from "react";
import {
  useParams,
  Switch,
  useRouteMatch,
  useLocation,
} from "react-router-dom";
import { PrivateRoute } from "../../components/PrivateRoute";
import { Activity } from "./activity";
import { Notes } from "./notes";
import { Fraud } from "./fraud";
import { OFAC } from "./ofac";
import { Messages } from "./messages";
import { WatchlistScreening } from "./watchlistScreening";
import { useDispatch, useSelector } from "react-redux";
import {
  selectApplicationDetail,
  getApplicationDetail,
  getApplicationActivity,
  getApplicationPdfs,
  getApplicationScreenshots,
  getApplicationTasks,
  getApplicationNotes,
  getApplicationMessages,
  getFraudEvaluation,
  getBusinessEvaluation,
  getFisGkycEvaluation,
  getAvailableDecisionStatuses,
  getDecisionStatus,
  getDecisionStatusLogs,
  getOnboardingApplicationUpdateLogs,
  getIPQSApplications,
  getIPQSFraudResults,
  getCreditReports,
  getOnboardingApplicationResponses,
  getWatchlistOrders,
  getIdvEvaluations,
  getWatchlistScreening,
} from "../../models/applications/applicationDetailSlice";
import { ApplicantSidebar } from "../../components/sidebar/applicantSidebar";
import { AdminSidebar } from "../../components/sidebar/adminSidebar";
import { selectMyUser } from "../../models/users/myUserSlice";
import { ApplicationNavbar } from "./applicationNavbar";
import { TaskViewer } from "../../components/sidebar/taskViewer";
import { GlobalFlashMessage } from "../../components/flashMessage";
import { Spinner } from "../../components/spinner";
import { asyncTimeout } from "../../utils/helpers";
import { mixpanel } from "../../utils/mixpanel";
import { EnvelopePaperFill, CheckCircleFill } from "react-bootstrap-icons";
import { GlobalKYC } from "./gkyc";
import { Task } from "../../models/applications/applicationInterfaces";
import { BagCheckFill } from "react-bootstrap-icons";
import { RootState } from "../../app/store";
import { BusinessGuard } from "./businessGuard";
import { CreditReports } from "./creditReports";
import { DecisionIntelligence } from "./decisionIntelligence-v2";
import SmallSpinner from "../../components/smallSpinner";
import PdfViewer from "../../components/sidebar/taskViewer/pdfViewer";
import PersistentBoaEmbed from "../../components/persistentBoaEmbed";
import { hasDisclosures } from "../../utils/disclosures";
import ScreenshotViewer from "../../components/sidebar/taskViewer/screenshotViewer";

export const selectOrg = (state: RootState) => state.organization;

export const ApplicationDetail: FC = () => {
  const { applicationId } = useParams<{
    applicationId: string;
  }>();
  const { path, url } = useRouteMatch();
  const { pathname } = useLocation();
  /**
   * Whether the Documents tab is the one showing.
   *
   * Read from the location rather than from a matched route, because the
   * disclosures panel that depends on it deliberately lives outside the Switch
   * — see where it is rendered below.
   */
  const isDocumentsTab = pathname.startsWith(`${url}/documents`);
  const dispatch = useDispatch();
  const {
    activity,
    decisionStatusLogs,
    onboardingApplicationUpdateLogs,
    application,
    availableDecisionStatuses,
    decisionStatus,
    tasks,
    pdfs,
    screenshots,
    notes,
    messages,
    fraudData,
    businessGuardData,
    fisGkycEvaluations,
    applicationsData,
    fraudResultsData,
    creditReportsData,
    watchlistData,
    idvData,
    onboardingApplicationData,
    flowData,
  } = useSelector(selectApplicationDetail);
  const organization = useSelector(selectOrg);
  const user = useSelector(selectMyUser);
  const permissions = useMemo(() => application?.permissions, [application]);

  const [frameLoaded, setFrameLoaded] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  // State for managing the last selected note of child component "Notes" to improve the user experience when switching between tabs.
  const [activeNote, setActiveNote] = useState(0);

  // Custom sorting function
  const sortByDocumentRequired = (taskA: Task, taskB: Task) => {
    if (taskA.documentRequired === taskB.documentRequired) {
      return 0; // Leave elements in their current order if documentRequired is the same
    }

    // If taskA's documentRequired is false, move it to the end
    if (taskA.documentRequired === false) {
      return 1;
    }

    // If taskB's documentRequired is false, move it to the beginning
    if (taskB.documentRequired === false) {
      return -1;
    }

    // If documentRequired is different for taskA and taskB, return a negative value to keep the original order
    return 0;
  };

  // Sort the tasks array using the custom sorting function
  const sortedTasks = tasks.slice().sort(sortByDocumentRequired);

  // Define the application as "locked" if its decision status is locked, or if it is archived.
  const applicationLocked =
    (organization.meta?.enableDecisioning === true
      ? decisionStatus?.locked === true
      : application && ["approved", "denied"].includes(application.status)) ||
    application?.archived === true;
  // Define if the user viewing the application is allowed to edit it.
  let isMyApplication: boolean | undefined = undefined;
  if (user && application) {
    isMyApplication = user?.userId === application?.user.userId;
  }
  const canViewDocuments = useMemo(
    () =>
      isMyApplication === true ||
      !!permissions?.includes("view-application-document"),
    [permissions, isMyApplication]
  );

  // Whether this application presented any disclosures. Answered from the flow
  // config and the saved responses, both already in the store, so the section
  // costs no request to decide on — which is what lets the panel behind it load
  // lazily, on the first visit to Documents, rather than on every page load.
  const disclosuresPresented = useMemo(
    () => hasDisclosures(flowData, onboardingApplicationData),
    [flowData, onboardingApplicationData]
  );

  // Stagger the loading of data to increase speed of initial load
  async function retrieveData() {
    await Promise.all([
      dispatch(getApplicationDetail(Number(applicationId))),
      dispatch(getOnboardingApplicationResponses(Number(applicationId))),
      dispatch(getApplicationActivity(Number(applicationId))),
      dispatch(getFraudEvaluation(applicationId)),
      dispatch(getFisGkycEvaluation(applicationId)),
      dispatch(getBusinessEvaluation(applicationId)),
      dispatch(getIPQSApplications(applicationId)),
      dispatch(getIPQSFraudResults(applicationId)),
      dispatch(getCreditReports(applicationId)),
      dispatch(getDecisionStatusLogs(Number(applicationId))),
      dispatch(getOnboardingApplicationUpdateLogs(Number(applicationId))),
      dispatch(getApplicationTasks(Number(applicationId))),
      dispatch(getApplicationPdfs(Number(applicationId))),
      dispatch(getApplicationScreenshots(Number(applicationId))),
      dispatch(getWatchlistOrders(applicationId)),
      dispatch(getIdvEvaluations(applicationId)),
    ]);
    setFrameLoaded(true);
    await Promise.all([
      dispatch(getDecisionStatus(Number(applicationId))),
      dispatch(getApplicationMessages(applicationId)),
      dispatch(getApplicationNotes({ id: applicationId })),
      dispatch(getAvailableDecisionStatuses(Number(applicationId))),
      // After the first batch: it reads the application's permissions.
      dispatch(getWatchlistScreening(applicationId)),
    ]);
    setDataLoaded(true);
  }
  useEffect(() => {
    if (isMyApplication) {
      mixpanel.track("Application Detail - Application View", {
        "User Id": user?.userId,
        "Company Name": organization.name,
        "Host Name": window.location.hostname,
        Page: window.location.href,
      });
    }
  }, [isMyApplication]);

  useEffect(() => {
    setFrameLoaded(false);
    setDataLoaded(false);
    retrieveData();
  }, [applicationId]);

  return (
    <div id="application-detail" className="row no-gutters">
      {/* CONTENT -------------------------------------------------------------------- */}
      <Spinner show={!frameLoaded} />
      {application &&
        activity &&
        decisionStatusLogs &&
        tasks &&
        isMyApplication !== undefined &&
        onboardingApplicationData &&
        flowData &&
        frameLoaded && (
          <>
            <div
              className="col-md-12 col-lg-9 order-md-1 order-lg-2 "
              style={{ borderRadius: "16px", background: "#f6f6f6" }}
            >
              {/* Tabs - "Items Needed From You" (etc) */}
              <ApplicationNavbar
                isMyApplication={isMyApplication}
                tasks={tasks}
                gkycAvailable={
                  fisGkycEvaluations && fisGkycEvaluations?.records?.length > 0
                    ? true
                    : false
                }
                creditReportsAvailable={
                  creditReportsData && creditReportsData?.length > 0
                    ? true
                    : false
                }
              />
              <GlobalFlashMessage />
              {/* Main Content */}
              <div
                className="p-5 bg-white"
                style={{
                  borderRadius: "16px 16px 0px 0px",
                  minHeight: "80vh",
                }}
              >
                <Switch>
                  <PrivateRoute path={`${path}/documents`}>
                    {!dataLoaded ? (
                      <SmallSpinner className="text-primary" />
                    ) : (
                      // Documents Dashboard
                      <div className="documents-container p-0 p-sm-1">
                        {/* Guard clause to prevent unauthorized access to the page */}
                        {canViewDocuments === false && (
                          <div className="d-flex align-items-center mb-5 text-white bg-primary p-3 rounded">
                            <BagCheckFill size={32} className="mr-3" />
                            <p className="mb-0 font-weight-bold">
                              You do not have permission to view this page.
                            </p>
                          </div>
                        )}
                        {/* If the application is locked, display a help message informing them of the state. */}
                        {applicationLocked && (
                          <div className="d-flex align-items-center mb-5 text-white bg-primary p-3 rounded">
                            <BagCheckFill size={32} className="mr-3" />
                            <p className="mb-0 font-weight-bold">
                              This application is locked and cannot be modified.
                            </p>
                          </div>
                        )}
                        {tasks.length === 0 &&
                        pdfs.length === 0 &&
                        screenshots.length === 0 &&
                        canViewDocuments ? (
                          <>
                            <h1
                              className="text-tertiary h2 mb-4"
                              style={{ fontWeight: 750 }}
                            >
                              <EnvelopePaperFill className="mr-2" />
                              Documents
                            </h1>
                            <div className="d-flex py-2">
                              <CheckCircleFill
                                className="mr-2"
                                style={{ color: "green" }}
                              />
                              Congratulations! This application requires no
                              additional documents
                              {application.status === "ready_for_partner_review"
                                ? " and is being reviewed."
                                : "."}
                            </div>
                          </>
                        ) : (
                          [
                            ...sortedTasks.map((task) => (
                              <div className="mb-5" key={task.id}>
                                <TaskViewer
                                  task={task}
                                  canUpload={
                                    !applicationLocked &&
                                    (isMyApplication === true ||
                                      !!permissions?.includes(
                                        "create-application-document"
                                      ))
                                  }
                                  canDelete={
                                    !applicationLocked &&
                                    (isMyApplication === true ||
                                      !!permissions?.includes(
                                        "delete-application-document"
                                      ))
                                  }
                                />
                              </div>
                            )),
                            <div className="mb-5">
                              <PdfViewer pdfs={pdfs} />
                            </div>,
                            <div className="mb-5">
                              <ScreenshotViewer screenshots={screenshots} />
                            </div>,
                          ]
                        )}
                      </div>
                    )}
                  </PrivateRoute>
                  <PrivateRoute path={`${path}/activity`}>
                    <Activity
                      activity={activity}
                      decisionStatusLogs={decisionStatusLogs}
                      onboardingApplicationUpdateLogs={
                        onboardingApplicationUpdateLogs
                      }
                      user={user}
                    />
                  </PrivateRoute>
                  <PrivateRoute path={`${path}/notes`}>
                    {!dataLoaded ? (
                      <SmallSpinner className="text-primary" />
                    ) : (
                      <Notes
                        applicant={application?.user}
                        notes={notes}
                        activeNote={activeNote}
                        setActiveNote={setActiveNote}
                      />
                    )}
                  </PrivateRoute>
                  <PrivateRoute path={`${path}/messages`}>
                    {!dataLoaded ? (
                      <SmallSpinner className="text-primary" />
                    ) : (
                      <Messages
                        applicant={application?.user}
                        messages={messages}
                        isMyApplication={isMyApplication}
                      />
                    )}
                  </PrivateRoute>
                  <PrivateRoute path={`${path}/fraud`}>
                    <Fraud fraudData={fraudData} />
                  </PrivateRoute>
                  <PrivateRoute path={`${path}/decision`}>
                    <DecisionIntelligence
                      effectivData={fraudData}
                      decisionStatusLogs={decisionStatusLogs}
                      applicationsData={applicationsData}
                      fraudResultsData={fraudResultsData}
                    />
                  </PrivateRoute>
                  <PrivateRoute path={`${path}/creditreports`}>
                    <CreditReports
                      creditReportsData={creditReportsData}
                      applicationsData={applicationsData}
                    />
                  </PrivateRoute>
                  <PrivateRoute path={`${path}/business`}>
                    <BusinessGuard
                      businessGuardData={businessGuardData}
                      applicationsData={applicationsData}
                      fraudResultsData={fraudResultsData}
                    />
                  </PrivateRoute>
                  <PrivateRoute path={`${path}/gkyc`}>
                    <GlobalKYC fisGkycEvaluations={fisGkycEvaluations} />
                  </PrivateRoute>
                  <PrivateRoute path={`${path}/watchlist`}>
                    <WatchlistScreening watchlistData={watchlistData} />
                  </PrivateRoute>
                  {/* Deprecated */}
                  <PrivateRoute path={`${path}/ofac`}>
                    <OFAC isAdmin={user?.isAdminPortalUser || false} />
                  </PrivateRoute>
                </Switch>

                {/* DISCLOSURES — the last section of the Documents tab.
                    The consent records for documents the applicant was SHOWN,
                    sitting after the stipulations they were asked FOR.

                    Rendered OUTSIDE the Switch on purpose, even though it
                    belongs to a tab that is inside it. A route unmounts when the
                    user picks another tab, and unmounting this iframe throws
                    away a booted boa app — so every return to Documents would
                    reboot it and refetch the record. Out here it mounts on the
                    first visit and then persists, hidden, for as long as this
                    application is open. See PersistentBoaEmbed.

                    Being a sibling of the Switch rather than a child of the
                    Documents route is also what positions it: when Documents is
                    active the Switch renders that tab's body and this lands
                    directly beneath it. That is exactly why it can only be the
                    tab's LAST section and cannot slot in between. */}
                {/* The section's title lives HERE, not in the embedded panel.
                    It is this app's own `text-tertiary h2` treatment with the
                    `.task-count` badge, matching the stipulation entries above
                    it — and building that inside boa would have meant porting
                    the legacy type scale into the app meant to outlive it. The
                    panel draws its content and nothing more, exactly as the
                    document-verification one does. */}
                {canViewDocuments &&
                  disclosuresPresented &&
                  isDocumentsTab &&
                  dataLoaded && (
                    <div className="d-flex align-items-center mb-4">
                      <div className="task-count complete">
                        <EnvelopePaperFill size={18} />
                      </div>
                      <p
                        className="text-tertiary h2 mb-0"
                        style={{ fontWeight: 750 }}
                      >
                        Disclosures
                      </p>
                    </div>
                  )}
                {canViewDocuments && disclosuresPresented && (
                  <PersistentBoaEmbed
                    // Mounts as soon as this page's own data has settled,
                    // whatever tab is showing — so boa boots and fetches while
                    // the reviewer is still reading Activity, and Documents is
                    // already warm on the first click.
                    //
                    // `disclosuresPresented` is what makes that affordable:
                    // only applications that actually have disclosures pay for
                    // a second app booting, and the cost is the one that used
                    // to be paid on first open rather than a new one. Gated on
                    // `dataLoaded` so the frame is not competing with this
                    // page's own requests.
                    active={disclosuresPresented && dataLoaded}
                    // Shown only on the Documents tab, and only once the
                    // stipulations are in. Revealed earlier, the section landed
                    // on screen FIRST and was then shoved down as the tasks
                    // arrived.
                    visible={isDocumentsTab && dataLoaded}
                    instanceKey={String(applicationId)}
                    title="Disclosures"
                    initialHeight={420}
                    path={`/organization/${organization?.slug}/applications/${applicationId}/documents/disclosures`}
                  />
                )}
              </div>
            </div>

            <div className="col-md-12 col-lg-3 order-md-2 order-lg-1">
              {isMyApplication ? (
                <ApplicantSidebar
                  application={application}
                  decisionStatus={decisionStatus}
                  onboardingApplicationData={onboardingApplicationData}
                  flowData={flowData}
                />
              ) : (
                <AdminSidebar
                  availableDecisionStatuses={availableDecisionStatuses}
                  decisionStatus={dataLoaded ? decisionStatus : undefined}
                  tasks={tasks}
                  retrieveData={retrieveData}
                  applicationLocked={applicationLocked}
                  onboardingApplicationData={onboardingApplicationData}
                  flowData={flowData}
                  financialApplication={applicationsData}
                />
              )}
            </div>
          </>
        )}
    </div>
  );
};
